import { render } from 'react-dom';
import { TonicProvider } from '@tonic-one/react';
import SolutionTrialContent from '../components/SolutionTrialContent';
import type { SolutionTrialOptions } from '@Types/credit';
import i18next from 'i18next';

/**
 * App Code for Cloud Email and Collaboration Protection
 * This should match the app code registered in Vision One Credit System
 */
const EMAIL_COLLABORATION_APP_CODE = 'V1_EMAILSECURITY';

/**
 * Activates solution trial using Vision One Credit System
 * Opens a modal managed by the credit team with customized content
 * After activation, the window will automatically reload
 *
 * @param onSuccessCallback - Optional callback to execute after successful activation
 * @throws Error if creditInAppFeature is not available
 */
export const activateSolutionTrial = async (
  onSuccessCallback?: () => Promise<void>
): Promise<void> => {
  // Check if credit feature is available
  if (!window.top?.creditInAppFeature?.solutionTrial) {
    console.error('creditInAppFeature.solutionTrial is not available');
    throw new Error(
      'Solution trial feature is not available. Please ensure you are running within Vision One platform.'
    );
  }

  try {
    // Prepare solution trial options
    const options: SolutionTrialOptions = {
      // Required fields
      appCode: EMAIL_COLLABORATION_APP_CODE,
      document,
      solutionName: i18next.t('welcome.solutionTrial.modal.solutionName'),
      description: i18next.t('welcome.solutionTrial.modal.description'),
      packages: i18next.t('welcome.solutionTrial.modal.packages'),
      // Customized content rendered in the modal

      content: (el: HTMLElement) => {
        render(
          <TonicProvider
            colorMode={{ defaultValue: 'dark' }}
            useCSSBaseline={false}
          >
            <SolutionTrialContent />
          </TonicProvider>,
          el
        );
      },
      reload: false, // Disable automatic reload to handle it manually
      // Optional: Handle post-activation logic before reload
      onSuccess: async () => {
        console.log('Solution trial activated successfully');
        // Execute callback if provided and wait for completion
        if (onSuccessCallback) {
          try {
            await onSuccessCallback();
          } catch (error) {
            console.error('Callback execution failed:', error);
          }
        }
        // async function automatically returns Promise
      },

      // Optional: Enable skip trial option
      // enableSkipTrial: false,
    };

    // Trigger solution trial modal
    window.top.creditInAppFeature.solutionTrial(options);
  } catch (error) {
    console.error('Failed to activate solution trial:', error);
    throw error;
  }
};

/**
 * Helper function to check if solution trial feature is available
 * Useful for conditional rendering of trial buttons
 *
 * @returns true if credit feature is available, false otherwise
 */
export const isSolutionTrialAvailable = (): boolean => {
  return !!window.top?.creditInAppFeature?.solutionTrial;
};
